import instance, { ApiResponse } from '../../api';
import { endpoints } from '../../endpoints.config';
import Button from '../../components/button/Button';
import React, { Dispatch, ReactNode, useState } from 'react';
import { JumioStage, JumioVerificationStatusResponse } from './jumioModels';
import { useDispatch } from 'react-redux';
import { setVerificationStage } from 'reducers/verification';
import { Toast } from 'helpers/toast';
import { CheckboxInput } from 'components/form/CheckboxInput';
import { ReactComponent as LinkSvg } from 'assets/icon-link.svg';
import { Form, Formik, FormikHelpers } from 'formik';

type Props = {};

export const JumioConsent = ({}: Props) => {
    const [submitting, setSubmitting] = useState(false);
    const dispatch = useDispatch();

    const initialValues = {
        terms: {
            eConsentDisclosure: false,
            bIbaneraPrivacyNotice: false,
            crbPrivacyNotice: false,
            depositAccountAgreement: false,
            usaPatriotActNotice: false,
        },
    };

    const updateUserTerms = async (values: (typeof initialValues)['terms']) => {
        return instance.post(endpoints.personalVerification.updateCustomerUserTerms, {
            bEConsentDisclosure: values.eConsentDisclosure,
            bIbaneraPrivacyNotice: values.crbPrivacyNotice,
            bCRBPrivacyNotice: values.crbPrivacyNotice,
            bDepositAccountAgreement: values.depositAccountAgreement,
            bUSAPatriotActNotice: values.usaPatriotActNotice,
        });
    };

    const fetchVerificationStatus = async () => {
        return instance.get<ApiResponse<JumioVerificationStatusResponse>>(
            endpoints.jumiomodule.verificationStatus,
            { params: { jumioConsent: true } }
        );
    };

    const handleSubmitConsent = async (
        values: typeof initialValues,
        dispatch: any,
        setSubmitting: (val: boolean) => void
    ) => {
        setSubmitting(true);
        try {
            await updateUserTerms(values.terms);
            const res = await fetchVerificationStatus();

            dispatch(
                setVerificationStage({
                    currentStage: res.data.details.status,
                    additionalData: res.data.details.additionalData,
                })
            );
        } catch (err) {
            Toast.openGenericErrorToast();
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div className="ConsentCollection">
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => handleSubmitConsent(values, dispatch, setSubmitting)}
            >
                {({ values, handleSubmit }) => {
                    const allChecked = Object.values(values.terms).every(Boolean);

                    return (
                        <Form>
                            <ButtonCheckboxInput
                                name={fieldName('eConsentDisclosure')}
                                label="E-Consent Disclosure"
                                text="I acknowledge and agree to the E-Consent Disclosure"
                                link="https://ibanera.sharefile.com/d-s0568de3c6a7f41af82101dbe99a3bfb9"
                            />
                            <ButtonCheckboxInput
                                name={fieldName('bIbaneraPrivacyNotice')}
                                label="Ibanera Privacy Notice"
                                text="I acknowledge and agree to the Ibanera Privacy Notice"
                                link="https://ibanera.sharefile.com/d-s747a7d820be545d1bd07ac88e48c44ad"
                            />
                            <ButtonCheckboxInput
                                name={fieldName('crbPrivacyNotice')}
                                label="CRB Privacy Notice"
                                text="I acknowledge and agree to the Cross River Bank Privacy Notice"
                                link="https://www.crossriver.com/legal/privacy-notice"
                            />
                            <ButtonCheckboxInput
                                name={fieldName('depositAccountAgreement')}
                                label="Deposit Account Agreement"
                                text="I acknowledge and agree to the Deposit Account Agreement"
                            />
                            <ButtonCheckboxInput
                                name={fieldName('usaPatriotActNotice')}
                                label="USA Patriot Act Notice"
                                text="I have read and acknowledge the USA PATRIOT Act Notice displayed above."
                                childComp={
                                    <div className="crb-terms">
                                        <hr />
                                        <p>
                                            <b>
                                                Important Information About Procedures for
                                                Conducting Transactions
                                            </b>
                                        </p>
                                        <p>
                                            To help the government fight the funding of terrorism
                                            and money laundering activities, federal law may require
                                            us to obtain, verify, and record information that
                                            identifies each person who conducts a transaction with
                                            the institution.
                                        </p>
                                        <p>
                                            <b>What this means for you</b>
                                        </p>
                                        <p>
                                            When you conduct a transaction with us, we may ask for
                                            your name, address, date of birth, and other information
                                            that will allow us to identify you, including your
                                            Social Security number. We may also ask to see your
                                            driver’s license or other identifying documents.
                                        </p>
                                    </div>
                                }
                            />

                            <p>
                                We use a third-party service "Jumio" to verify your identity. By
                                clicking “Continue” you consent to Jumio collecting, processing, and
                                sharing your personal information, which may include biometric data,
                                pursuant to its{' '}
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://www.jumio.com/legal-information/privacy-notices/jumio-corp-privacy-policy-for-online-services/"
                                >
                                    Privacy Notice.
                                </a>
                            </p>

                            <Button
                                type="submit"
                                variety="full"
                                disabled={!allChecked || submitting}
                                onClick={() => handleSubmit()}
                            >
                                Continue
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

const fieldName = (name: string) => `terms.${name}`;

export const ButtonCheckboxInput = ({
    name,
    label,
    text,
    showErrorMessage = false,
    link,
    childComp,
}: {
    name: string;
    label?: string;
    text: string;
    showErrorMessage?: boolean;
    link?: string;
    childComp?: ReactNode;
}) => {
    return (
        <div className="FormBox">
            {label && (
                <div style={{ display: 'flex', gap: 5 }}>
                    <div className="FormLabel">
                        <label>{label}</label>
                    </div>
                    {link && (
                        <a href={link} target="_blank" style={{ marginTop: '-3px' }}>
                            <LinkSvg />
                        </a>
                    )}
                </div>
            )}
            <div className={`FormField ButtonCheckboxInput`}>
                <CheckboxInput name={name} label={text} />
                {childComp}
            </div>
        </div>
    );
};
